<template>
  <section class="th">
    <header class="th-head">
      <div>
        <h3>История рейса <KbHint article="trip-history" /></h3>
        <p class="th-sub" v-if="data">
          {{ data.summary.total }} событий<span v-if="data.summary.last_at"> · последнее в {{ time(data.summary.last_at) }}</span>
        </p>
      </div>
      <button class="th-btn" :disabled="loading" @click="load">{{ loading ? 'Обновляем…' : 'Обновить' }}</button>
    </header>

    <div v-if="data && data.summary.open_problem" class="th-problem">
      <b>Не уходит в TMS:</b> {{ data.summary.open_problem }}
    </div>

    <nav v-if="data && data.events.length" class="th-filters">
      <button v-for="f in filters" :key="f.key" class="th-chip" :class="{ 'th-chip--on': filter === f.key }"
              :disabled="!counts[f.key] && f.key !== 'all'" @click="filter = f.key">
        {{ f.title }}<i v-if="counts[f.key]">{{ counts[f.key] }}</i>
      </button>
    </nav>

    <p v-if="error" class="th-error">{{ error }}</p>
    <p v-else-if="loading && !data" class="th-muted">Собираем историю…</p>
    <p v-else-if="data && !data.events.length" class="th-muted">Событий по рейсу пока нет.</p>

    <div v-for="group in groups" :key="group.key" class="th-day">
      <div class="th-day__title">{{ group.title }}</div>
      <transition-group name="th-item" tag="ol" class="th-list">
        <li v-for="(e, i) in group.items" :key="group.key + i + e.at" class="th-item" :class="'th-item--' + e.level">
          <span class="th-dot" aria-hidden="true"></span>
          <span class="th-time">{{ time(e.at) }}</span>
          <div class="th-body">
            <div class="th-title">
              {{ e.title }}
              <span class="th-cat">{{ e.category_title }}</span>
              <span v-if="e.count > 1" class="th-repeat">×{{ e.count }}</span>
            </div>
            <p v-if="e.text" class="th-text">{{ e.text }}</p>
            <p class="th-meta">
              {{ e.actor }}<span v-for="(d, j) in e.details" :key="j"> · {{ d }}</span>
              <span v-if="e.count > 1"> · {{ repeat(e) }}</span>
            </p>
          </div>
        </li>
      </transition-group>
    </div>

    <button v-if="hidden > 0" class="th-more" @click="limit += 30">Показать ещё {{ Math.min(hidden, 30) }}</button>
  </section>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import KbHint from '@/components/KbHint.vue'
import { fetchTripHistory } from '@/backend/tripHistoryApi'
import {
  FILTERS, countBy, filterEvents, formatTime, groupByDay, repeatText,
} from '@/pages/trip/helpers/tripHistory.mjs'

export default {
  name: 'TripHistory',
  components: { KbHint },
  props: { tripId: { type: [Number, String], required: true } },
  setup(props) {
    const data = ref(null)
    const loading = ref(false)
    const error = ref('')
    const filter = ref('all')
    const limit = ref(20)

    const load = async () => {
      loading.value = true
      error.value = ''
      try {
        data.value = await fetchTripHistory(props.tripId)
      } catch (e) {
        error.value = e?.response?.data?.detail || e?.message || 'История не загрузилась. Повторите через минуту.'
      } finally {
        loading.value = false
      }
    }

    const filtered = computed(() => filterEvents(data.value?.events || [], filter.value))
    const groups = computed(() => groupByDay(filtered.value.slice(0, limit.value)))
    const hidden = computed(() => Math.max(0, filtered.value.length - limit.value))
    const counts = computed(() => countBy(data.value?.events || []))

    watch(filter, () => { limit.value = 20 })
    watch(() => props.tripId, load)
    onMounted(load)

    return {
      data, loading, error, filter, limit, groups, hidden, counts, filters: FILTERS, load,
      time: formatTime, repeat: repeatText,
    }
  },
}
</script>

<style scoped>
.th { --gold: #f2b705; --ink: #1f2430; --muted: #6b7280; --line: #e7e5df;
  margin: 16px 0; padding: 18px 20px; background: #fff; border: 1px solid var(--line); border-radius: 16px; color: var(--ink); }
.th-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.th-head h3 { margin: 0; font-size: 17px; display: flex; align-items: center; gap: 8px; }
.th-sub { margin: 4px 0 0; color: var(--muted); font-size: 12px; }
.th-btn { border: 1px solid var(--line); background: #fff; border-radius: 10px; padding: 6px 12px; font-size: 12px; cursor: pointer; }
.th-btn:hover { border-color: var(--gold); background: #fffaf0; }
.th-problem { margin: 12px 0 0; padding: 10px 12px; border-radius: 12px; background: #fef2f2; border: 1px solid #fecaca;
  color: #7f1d1d; font-size: 13px; }
.th-filters { display: flex; flex-wrap: wrap; gap: 6px; margin: 12px 0 4px; }
.th-chip { border: 1px solid var(--line); background: #fff; border-radius: 999px; padding: 4px 10px; font-size: 12px;
  cursor: pointer; display: inline-flex; gap: 6px; align-items: center; transition: all .2s; }
.th-chip i { font-style: normal; color: var(--muted); }
.th-chip--on { background: var(--ink); color: #fff; border-color: var(--ink); }
.th-chip--on i { color: var(--gold); }
.th-chip:disabled { opacity: .4; cursor: default; }
.th-day { margin-top: 14px; }
.th-day__title { font-size: 12px; text-transform: uppercase; letter-spacing: .06em; color: var(--muted); margin-bottom: 6px; }
.th-list { list-style: none; margin: 0; padding: 0 0 0 4px; border-left: 2px solid #f1efe8; }
.th-item { position: relative; display: grid; grid-template-columns: 48px 1fr; gap: 10px; padding: 8px 0 8px 14px; }
.th-dot { position: absolute; left: -7px; top: 13px; width: 12px; height: 12px; border-radius: 50%; background: #cbd5e1;
  box-shadow: 0 0 0 3px #fff; }
.th-item--success .th-dot { background: #22c55e; }
.th-item--warning .th-dot { background: var(--gold); }
.th-item--error .th-dot { background: #ef4444; }
.th-time { font-variant-numeric: tabular-nums; color: var(--muted); font-size: 13px; padding-top: 1px; }
.th-title { font-weight: 600; font-size: 14px; display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.th-cat { font-weight: 500; font-size: 11px; color: var(--muted); background: #f5f5f2; border-radius: 999px; padding: 1px 8px; }
.th-repeat { font-size: 11px; font-weight: 700; color: #7f1d1d; background: #fee2e2; border-radius: 999px; padding: 1px 8px; }
.th-text { margin: 3px 0 0; font-size: 13px; color: #374151; line-height: 1.45; }
.th-meta { margin: 3px 0 0; font-size: 12px; color: var(--muted); }
.th-more { margin-top: 10px; border: 0; background: none; color: #4f46e5; cursor: pointer; font-size: 13px; }
.th-error { color: #b91c1c; }
.th-muted { color: var(--muted); }
.th-item-enter-active { transition: all .25s ease; }
.th-item-enter-from { opacity: 0; transform: translateY(6px); }
@media (prefers-reduced-motion: reduce) { .th-item-enter-active, .th-chip { transition: none; } }
@media (max-width: 640px) { .th { padding: 14px; } .th-item { grid-template-columns: 40px 1fr; } }
</style>
